import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Chip, 
  Divider,
  Button
} from '@mui/material';
import {
  Restore as RestoreIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatStoreSlugHost } from '../../utils/storeSlugUtils';
import { BRAND } from '../../theme/brandTokens';

const StoreReview = ({ formData, onRollback, showRollback, loading, saving, mode = 'admin' }) => {
  const { t } = useTranslation();
  const isSelfService = mode === 'self-service';

  const formatBusinessHours = (hours) => {
    if (hours.closed) return t('common.closed');
    if (!hours.periods || hours.periods.length === 0) return t('common.closed');
    return hours.periods.map(p => `${p.from} - ${p.to}`).join(', ');
  };

  const getDisplayValue = (value) => {
    if (value === null || value === undefined) {
      return t('common.notProvided');
    }

    if (typeof value === 'string') {
      return value.trim() ? value : t('common.notProvided');
    }

    return value;
  };

  const renderReviewField = ({ label, value, size }) => (
    <Grid size={size}>
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
        {getDisplayValue(value)}
      </Typography>
    </Grid>
  );

  const reviewGroupSx = {
    p: { xs: 2, sm: 2.5 },
    border: `1px solid ${BRAND.line}`,
    borderRadius: 2,
    backgroundColor: BRAND.surface,
    height: '100%'
  };

  return (
    <Box id="review-step">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: BRAND.text, fontWeight: 700 }}>
          {t('storeRegistrationSteps.step5.title')}
        </Typography>
        {showRollback && onRollback && (
          <Button
            variant="outlined"
            onClick={onRollback}
            disabled={loading || saving}
            startIcon={<RestoreIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: '#9e9e9e',
              color: '#616161',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.9rem',
              '&:hover': {
                borderColor: '#757575',
                backgroundColor: 'rgba(158, 158, 158, 0.08)',
                color: '#424242',
              },
              '&:disabled': {
                borderColor: 'rgba(158, 158, 158, 0.3)',
                color: 'rgba(158, 158, 158, 0.5)',
              }
            }}
          >
            {t('storeUpdate.discardChanges') || 'Discard Changes'}
          </Button>
        )}
      </Box>

      <Box id="review-step-content" sx={{
        border: `1px solid ${BRAND.line}`,
        borderRadius: 2,
        p: 3,
        backgroundColor: BRAND.background
      }}>
        {/* Basic Info */}
        <Box id="review-basic-info" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2 }}>
            {t('storeRegistrationSteps.step1.title')}
          </Typography>
          <Grid container spacing={2.5}>
            {renderReviewField({
              label: t('storeRegistration.name'),
              value: formData.name,
              size: { xs: 12, sm: 6, md: 3 }
            })}
            {renderReviewField({
              label: t('storeRegistration.basicInfo.slug'),
              value: formatStoreSlugHost(formData.slug),
              size: { xs: 12, sm: 6, md: 3 }
            })}
            {renderReviewField({
              label: t('storeRegistration.basicInfo.currency'),
              value: formData.currency,
              size: { xs: 12, sm: 6, md: 2 }
            })}
            {renderReviewField({
              label: t('storeRegistration.basicInfo.timeZone'),
              value: formData.timeZone,
              size: { xs: 12, sm: 6, md: 3 }
            })}
            {renderReviewField({
              label: t('storeRegistration.basicInfo.storeLanguage'),
              value: formData.storeLanguage ? t(`common.languages.${formData.storeLanguage}`) : null,
              size: { xs: 12, sm: 6, md: 4 }
            })}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary">{t('storeRegistration.about')}:</Typography>
              <Box id="about-review" sx={{ mt: 1 }}>
                {formData.supportedLanguages.map(lang => (
                  <Box key={lang} sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: BRAND.accentText }}>
                      {lang.toUpperCase()}:
                    </Typography>
                    <Typography variant="body1" sx={{ ml: 1 }}>
                      {getDisplayValue(formData.about[lang])}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary">{t('storeRegistration.supportedLanguages')}:</Typography>
              <Box id="supported-languages-review" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {formData.supportedLanguages.map(lang => (
                  <Chip
                    key={lang}
                    label={lang.toUpperCase()}
                    size="small"
                    sx={{ backgroundColor: BRAND.accent, color: BRAND.onAccent }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider id="review-divider-1" sx={{ my: 3 }} />

        {/* Contact Info */}
        <Box id="review-contact-info" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2 }}>
            {t('storeRegistrationSteps.step2.title')}
          </Typography>
          <Grid container spacing={2.5}>
            {isSelfService ? (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={reviewGroupSx}>
                    <Typography variant="subtitle2" sx={{ color: BRAND.primaryText, fontWeight: 700, mb: 2 }}>
                      {t('storeRegistration.ownerSectionTitle')}
                    </Typography>
                    <Grid container spacing={2}>
                      {renderReviewField({
                        label: t('storeRegistration.ownerName'),
                        value: formData.ownerName,
                        size: { xs: 12, sm: 6 }
                      })}
                      {renderReviewField({
                        label: t('storeRegistration.ownerPhone'),
                        value: formData.ownerPhoneNumber
                          ? `${formData.ownerPhoneCountryCode}${formData.ownerPhoneNumber}`
                          : null,
                        size: { xs: 12, sm: 6 }
                      })}
                      {renderReviewField({
                        label: t('storeRegistration.ownerEmail'),
                        value: formData.ownerEmail,
                        size: { xs: 12 }
                      })}
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={reviewGroupSx}>
                    <Typography variant="subtitle2" sx={{ color: BRAND.primaryText, fontWeight: 700, mb: 2 }}>
                      {t('storeRegistration.storeContactSectionTitle')}
                    </Typography>
                    <Grid container spacing={2}>
                      {renderReviewField({
                        label: t('storeRegistration.storePhone'),
                        value: formData.phoneNumber
                          ? `${formData.phoneCountryCode}${formData.phoneNumber}`
                          : null,
                        size: { xs: 12, sm: 6 }
                      })}
                      {renderReviewField({
                        label: t('storeRegistration.website'),
                        value: formData.website,
                        size: { xs: 12, sm: 6 }
                      })}
                      {renderReviewField({
                        label: t('storeRegistration.googleReviewUrl'),
                        value: formData.googleReviewUrl,
                        size: { xs: 12 }
                      })}
                      {renderReviewField({
                        label: t('storeRegistration.storeEmail'),
                        value: formData.email,
                        size: { xs: 12 }
                      })}
                    </Grid>
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                {renderReviewField({
                  label: t('storeRegistration.email'),
                  value: formData.email,
                  size: { xs: 12, sm: 6, md: 4 }
                })}
                {renderReviewField({
                  label: t('storeRegistration.phone'),
                  value: formData.phoneNumber
                    ? `${formData.phoneCountryCode}${formData.phoneNumber}`
                    : null,
                  size: { xs: 12, sm: 6, md: 4 }
                })}
                {renderReviewField({
                  label: t('storeRegistration.website'),
                  value: formData.website,
                  size: { xs: 12, md: 4 }
                })}
                {renderReviewField({
                  label: t('storeRegistration.googleReviewUrl'),
                  value: formData.googleReviewUrl,
                  size: { xs: 12 }
                })}
              </>
            )}
          </Grid>
        </Box>

        <Divider id="review-divider-2" sx={{ my: 3 }} />

        {/* Location Info */}
        <Box id="review-location-info" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2 }}>
            {t('storeRegistrationSteps.step3.title')}
          </Typography>
          <Grid container spacing={2.5}>
            {renderReviewField({
              label: t('storeRegistration.address'),
              value: formData.address,
              size: { xs: 12, md: 8 }
            })}
            {formData.door && (
              renderReviewField({
                label: t('storeRegistration.door'),
                value: formData.door,
                size: { xs: 12, md: 4 }
              })
            )}
            {formData.additionalInfo && (
              renderReviewField({
                label: t('storeRegistration.additionalInfo'),
                value: formData.additionalInfo,
                size: { xs: 12 }
              })
            )}
            {renderReviewField({
              label: t('storeRegistration.city'),
              value: formData.city,
              size: { xs: 12, sm: 6, md: 3 }
            })}
            {renderReviewField({
              label: t('storeRegistration.state'),
              value: formData.state,
              size: { xs: 12, sm: 6, md: 3 }
            })}
            {renderReviewField({
              label: t('storeRegistration.zipCode'),
              value: formData.zipCode,
              size: { xs: 12, sm: 6, md: 3 }
            })}
            {renderReviewField({
              label: t('storeRegistration.country'),
              value: formData.country,
              size: { xs: 12, sm: 6, md: 3 }
            })}
          </Grid>
        </Box>

        {!isSelfService && (
          <>
            <Divider id="review-divider-3" sx={{ my: 3 }} />

            {/* Business Hours */}
            <Box id="review-services-hours">
              <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2 }}>
                {t('storeRegistrationSteps.step4.title')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">{t('storeRegistration.businessHours.title')}:</Typography>
                  <Box id="business-hours-review" sx={{ mt: 1 }}>
                    {Object.entries(formData.businessHours).map(([day, hours]) => (
                      <Box key={day} id={`business-hours-review-day-${day}`} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500, mr: 1.5 }}>
                          {t(`common.days.${day}`)}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatBusinessHours(hours)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default StoreReview;
